import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ModelIcon, VideoIcon, UsersIcon } from "../icons/icons";

// ─── Floating Particle ───────────────────────────────────────────────────────
const FloatingParticle: React.FC<{ x: number; delay: number; color?: string }> = ({ x, delay, color = "#0072CE" }) => (
    <motion.div
        className="absolute w-1 h-1 rounded-full pointer-events-none"
        style={{ left: `${x}%`, bottom: "10%", backgroundColor: color, opacity: 0.6 }}
        animate={{ y: [0, -45, 0], opacity: [0, 0.7, 0], scale: [0, 1.2, 0] }}
        transition={{ duration: 2, delay, repeat: Infinity, ease: "easeInOut" }}
    />
);

// ─── Feature Chip Component ──────────────────────────────────────────────────
interface FeatureChipProps {
    icon: React.ReactNode;
    title: string;
    description: string;
    index: number;
}

const FeatureChip: React.FC<FeatureChipProps> = ({ icon, title, description, index }) => {
    const [hovered, setHovered] = useState(false);
    const [particles, setParticles] = useState<{ id: number; x: number }[]>([]);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const spawnParticles = () => {
        const ps = Array.from({ length: 3 }, (_, i) => ({ id: Date.now() + i, x: 20 + Math.random() * 60 }));
        setParticles(ps);
        timerRef.current = setTimeout(() => setParticles([]), 2100);
    };

    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
    }, []);

    return (
        <motion.div
            className="relative flex items-center gap-3 cursor-pointer overflow-hidden rounded-2xl p-3"
            style={{
                background: hovered ? "linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(239,246,255,0.8) 100%)" : "transparent",
                border: `1px solid ${hovered ? "rgba(0,114,206,0.25)" : "transparent"}`,
                backdropFilter: hovered ? "blur(12px)" : "none",
                WebkitBackdropFilter: hovered ? "blur(12px)" : "none",
                boxShadow: hovered ? "0 8px 24px -6px rgba(0,114,206,0.18)" : "none",
                transition: "all 0.3s ease",
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 1.4 + index * 0.1 }}
            whileHover={{ scale: 1.04, y: -4 }}
            whileTap={{ scale: 0.97 }}
            onHoverStart={() => {
                setHovered(true);
                spawnParticles();
            }}
            onHoverEnd={() => setHovered(false)}
        >
            {/* Particles */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden">
                {particles.map((p) => (
                    <FloatingParticle key={p.id} x={p.x} delay={0} color="#0072CE" />
                ))}
            </div>

            {/* Top shimmer divider */}
            <motion.div
                className="absolute top-0 left-4 right-4 h-[1.5px] rounded-full"
                style={{ background: "linear-gradient(90deg, transparent, #0072CE, transparent)" }}
                animate={{ opacity: hovered ? 0.7 : 0, scaleX: hovered ? 1 : 0 }}
                transition={{ duration: 0.3 }}
            />

            {/* Icon */}
            <motion.div
                className="flex-shrink-0 relative"
                animate={{ rotate: hovered ? [0, -8, 8, 0] : 0, scale: hovered ? 1.1 : 1 }}
                transition={{ duration: 0.4 }}
            >
                {hovered && (
                    <motion.div
                        className="absolute inset-0 rounded-xl pointer-events-none"
                        style={{ border: "1.5px solid rgba(0,114,206,0.35)" }}
                        initial={{ scale: 1, opacity: 0.5 }}
                        animate={{ scale: 1.5, opacity: 0 }}
                        transition={{ duration: 0.9, repeat: Infinity }}
                    />
                )}
                {icon}
            </motion.div>

            {/* Text */}
            <div className="flex flex-col">
                <motion.h3
                    className="font-semibold mb-0.5 text-sm transition-colors duration-300"
                    animate={{ color: hovered ? "#0072CE" : "#1e293b" }}
                >
                    {title}
                </motion.h3>
                <motion.p
                    className="text-xs leading-relaxed transition-colors duration-300"
                    animate={{ color: hovered ? "#64748b" : "#5D697F" }}
                >
                    {description}
                </motion.p>
            </div>
        </motion.div>
    );
};

const ringsData = [
    {
        background: "../../media/background4.webp",
        shadow: "../../media/shadow4.webp",
        ring: "../../media/ring4.webp",
        shadowStyle: { x: -60, y: -125, scale: 3.5 },
        ringStyle: { x: -73, y: -175, scale: 4 },
        ringStyleMobile: { x: -60, y: -195, scale: 4 },
        ringWidth: "55%",
    },
    {
        background: "../../media/background3.webp",
        shadow: "../../media/shadow3.webp",
        ring: "../../media/ring3.webp",
        shadowStyle: { x: -70, y: 110, scale: 4 },
        ringStyle: { x: -65, y: 70, scale: 2 },
        ringStyleMobile: { x: -55, y: 150, scale: 2 },
        ringWidth: "68%",
    },
    {
        background: "../../media/background2.webp",
        shadow: "../../media/shadow2.webp",
        ring: "../../media/ring2.webp",
        shadowStyle: { x: -70, y: -120, scale: 4 },
        ringStyle: { x: -68, y: -192, scale: 2 },
        ringStyleMobile: { x: -55, y: -172, scale: 2 },
        ringWidth: "64%",
    },
    {
        background: "../../media/background1.webp",
        shadow: "../../media/shadow.webp",
        ring: "../../media/ring1.webp",
        shadowStyle: { x: -71, y: 114, scale: 4 },
        ringStyle: { x: -67, y: 50, scale: 2 },
        ringStyleMobile: { x: -55, y: 150, scale: 2 },
        ringWidth: "72%",
    },
];

const RingHeroCard: React.FC<{
    ring: typeof ringsData[0];
    index: number;
    isMobile: boolean;
    t: (key: string) => string;
}> = ({ ring, index, isMobile, t }) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isBgLoaded, setIsBgLoaded] = useState(false);
    const [isRingLoaded, setIsRingLoaded] = useState(false);
    const [particles, setParticles] = useState<{ id: number; x: number; y: number }[]>([]);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const currentStyle = isMobile ? ring.ringStyleMobile || ring.ringStyle : ring.ringStyle;

    const getTransformStyle = (styleData: { x: number; y: number; scale: number }) => {
        return `translate(${styleData.x}%, ${styleData.y}%) scale(${styleData.scale})`;
    };

    useEffect(() => {
        const imgBg = new Image();
        imgBg.src = ring.background;
        if (imgBg.complete) setIsBgLoaded(true);
        else imgBg.onload = () => setIsBgLoaded(true);

        const imgRing = new Image();
        imgRing.src = ring.ring;
        if (imgRing.complete) setIsRingLoaded(true);
        else imgRing.onload = () => setIsRingLoaded(true);
    }, [ring.background, ring.ring]);

    const spawnParticles = () => {
        const ps = Array.from({ length: 4 }, (_, i) => ({
            id: Date.now() + i,
            x: 20 + Math.random() * 60,
            y: 35 + Math.random() * 35,
        }));
        setParticles(ps);
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setParticles([]), 1800);
    };

    useEffect(() => () => {
        if (timerRef.current) clearTimeout(timerRef.current);
    }, []);

    const isAllLoaded = isBgLoaded && isRingLoaded;

    const baseTransform = `translate(${currentStyle.x}%, ${currentStyle.y}%) scale(${currentStyle.scale})`;
    const hoverTransform = `translate(${currentStyle.x}%, ${currentStyle.y - 6}%) scale(${currentStyle.scale * 1.08}) rotate(${index % 2 === 0 ? -3 : 3}deg)`;
    const loadTransform = `translate(${currentStyle.x}%, ${currentStyle.y + 8}%) scale(${currentStyle.scale * 0.9})`;

    const imageTransform = !isRingLoaded 
        ? loadTransform 
        : isHovered 
            ? hoverTransform 
            : baseTransform;

    return (
        <div
            className="relative w-1/4 cursor-pointer group select-none"
            onMouseEnter={() => {
                setIsHovered(true);
                spawnParticles();
            }}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div className="relative w-full h-full">
                {/* Skeleton Shimmer Load State */}
                {!isAllLoaded && (
                    <div className="absolute inset-0 z-40 flex items-center justify-center rounded-3xl overflow-hidden bg-slate-100/70 backdrop-blur-[2px]">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-100/60 to-transparent animate-pulse" />
                        <div className="w-5 h-5 border-2 border-[#0072CE] border-t-transparent rounded-full animate-spin relative z-10" />
                    </div>
                )}

                {/* Background Capsule Image */}
                <div className="relative rounded-3xl overflow-hidden">
                    <img
                        src={ring.background}
                        alt={t("images.ringBackground")}
                        className="w-full h-full max-w-[112px] object-contain relative z-10 mx-auto transition-all duration-300"
                        style={{
                            opacity: isBgLoaded ? 1 : 0,
                            filter: isHovered
                                ? "drop-shadow(0 10px 24px rgba(0,114,206,0.35)) brightness(1.05)"
                                : "drop-shadow(0 2px 8px rgba(0,0,0,0.06)) brightness(1)",
                        }}
                    />

                    {/* Subtle Shimmer Sweep on Hover */}
                    <div
                        className="absolute inset-0 z-15 pointer-events-none bg-gradient-to-tr from-transparent via-white/40 to-transparent transition-all duration-700 ease-in-out"
                        style={{
                            transform: isHovered ? "translateX(100%)" : "translateX(-100%)",
                            opacity: isHovered ? 1 : 0,
                        }}
                    />
                </div>

                {/* Shadow Layer (Exact transform preserved) */}
                <div className="absolute inset-0 z-20 pointer-events-none">
                    <img
                        src={ring.shadow}
                        alt={t("images.ringShadow")}
                        className="absolute top-1/2 left-1/2 w-[55%] h-auto transition-all duration-300"
                        style={{
                            filter: "blur(1.5px)",
                            transform: getTransformStyle(ring.shadowStyle),
                            opacity: isHovered ? 0.95 : 0.65,
                        }}
                    />
                </div>

                {/* Floating Micro Sparkles */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden z-25">
                    {particles.map((p) => (
                        <motion.div
                            key={p.id}
                            className="absolute w-1.5 h-1.5 rounded-full"
                            style={{
                                left: `${p.x}%`,
                                top: `${p.y}%`,
                                backgroundColor: "#0072CE",
                                boxShadow: "0 0 8px #0072CE",
                            }}
                            initial={{ opacity: 0, scale: 0, y: 0 }}
                            animate={{ opacity: [0, 0.9, 0], scale: [0, 1.2, 0], y: -22 }}
                            transition={{ duration: 1.4, ease: "easeOut" }}
                        />
                    ))}
                </div>

                {/* Smart Ring Product Image with Load Entry Animation & Springy Micro Hover */}
                <img
                    src={ring.ring}
                    alt={t("images.smartRing")}
                    className="absolute top-1/2 left-1/2"
                    style={{
                        transform: imageTransform,
                        width: ring.ringWidth,
                        zIndex: 30,
                        opacity: isRingLoaded ? 1 : 0,
                        transition: "transform 0.45s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.5s ease-out, filter 0.35s ease",
                        filter: isHovered
                            ? "drop-shadow(0 14px 26px rgba(0,114,206,0.45)) brightness(1.08)"
                            : "drop-shadow(0 4px 10px rgba(0,0,0,0.15)) brightness(1)",
                    }}
                />
            </div>
        </div>
    );
};

interface HeroSectionProps {
    onCompareClick: () => void;
    onRecommendedClick?: () => void;
}

const HeroSection: React.FC<HeroSectionProps> = ({ onCompareClick, onRecommendedClick }) => {
    const { t, i18n } = useTranslation("ringHero");
    const isRTL = ["fa", "ar"].includes(i18n.language);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        checkMobile();
        window.addEventListener("resize", checkMobile);
        return () => window.removeEventListener("resize", checkMobile);
    }, []);

    const handleSmoothScroll = () => {
        const targetElement = document.getElementById("comparison-section") || document.querySelector('[data-section="comparison"]') || document.querySelector(".comparison-section");

        if (targetElement) {
            targetElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
                inline: "nearest",
            });
        } else {
            window.scrollTo({
                top: window.innerHeight,
                behavior: "smooth",
            });
        }

        onCompareClick();
    };

    const handleScrollToRecommended = () => {
        const targetElement =
            document.getElementById("curated-showcase") ||
            document.querySelector('[data-section="curated-showcase"]');

        if (targetElement) {
            targetElement.scrollIntoView({
                behavior: "smooth",
                block: "start",
                inline: "nearest",
            });
        }

        if (onRecommendedClick) {
            onRecommendedClick();
        }
    };

    const features = [
        {
            title: t("features.multiUser.title"),
            description: t("features.multiUser.description"),
            icon: <UsersIcon />,
        },
        {
            title: t("features.aiAnalysis.title"),
            description: t("features.aiAnalysis.description"),
            icon: (
                <div className="w-[52px] h-[52px] rounded-xl bg-gradient-to-b from-blue-50 to-white/0 flex items-center justify-center">
                    <img src="/icon/ai.svg" alt={t("features.aiAnalysis.iconAlt")} className="w-6 h-6" />
                </div>
            ),
        },
        {
            title: t("features.monitoring24.title"),
            description: t("features.monitoring24.description"),
            icon: (
                <div className="w-[52px] h-[52px] rounded-xl bg-gradient-to-b from-blue-50 to-white/0 flex items-center justify-center">
                    <img src="/icon/payesh.svg" alt={t("features.monitoring24.iconAlt")} className="w-6 h-6" />
                </div>
            ),
        },
    ];

    const buttons = [
        {
            text: t("buttons.compare"),
            icon: <ModelIcon />,
            className: "bg-gradient-to-r from-[#0038A8] to-[#0072CE] text-white shadow-lg shadow-blue-500/20",
            shadow: "0 10px 25px -5px rgba(0, 114, 206, 0.4)",
            onClick: handleSmoothScroll,
        },
        {
            text: t("buttons.recommended"),
            icon: <VideoIcon />,
            className: "bg-white border border-slate-200 text-[#293048]",
            shadow: "0 10px 25px -5px rgba(107, 114, 128, 0.2)",
            onClick: handleScrollToRecommended,
        },
    ];

    const getTransformStyle = (styleData: { x: number; y: number; scale: number }) => {
        return `translate(${styleData.x}%, ${styleData.y}%) scale(${styleData.scale})`;
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="relative pt-0 sm:pt-16 overflow-x-hidden"
            dir={isRTL ? "rtl" : "ltr"}
        >
            {/* Main Container */}
            <div className="container px-4 sm:px-10 pb-4 lg:pb-12 pt-4 sm:pt-20 flex flex-col md:flex-row-reverse items-center justify-between gap-5 sm:gap-0">
                {/* Product Images (Left in LTR, Right in RTL) */}
                <div className="w-full md:w-1/2 mb-6 md:mb-0 relative sm:px-2">
                    <div className="flex space-x-1 relative justify-center" dir="ltr">
                        {ringsData.map((ring, index) => (
                            <RingHeroCard key={index} ring={ring} index={index} isMobile={isMobile} t={t} />
                        ))}
                    </div>
                </div>

                {/* Text Content */}
                <div className="w-full md:w-1/2 text-center sm:text-start z-10">
                    <div className="max-w-lg mx-auto sm:mx-0">
                        {/* Badge */}
                        <div className="border border-blue-200 bg-blue-50/50 rounded-full w-fit py-1 px-3 mx-auto sm:mx-0 mb-3 sm:mb-8">
                            <p className="text-[#0038A8] font-medium text-xs sm:text-sm">{t("badge")}</p>
                        </div>

                        {/* Title */}
                        <h1 className="text-2xl sm:text-4xl font-bold mb-2 sm:mb-4 text-slate-900">{t("title")}</h1>
                        <h2 className="text-xl sm:text-4xl mb-3 sm:mb-6 font-medium">
                            {t("subtitle.part1")} <span className="bg-gradient-to-r from-[#0038A8] to-[#0072CE] bg-clip-text text-transparent">{t("subtitle.highlight")}</span>
                        </h2>
                        <p className="text-[#293048] mb-5 sm:mb-8 text-sm sm:text-lg leading-relaxed">{t("description")}</p>

                        {/* Buttons */}
                        <div className="flex flex-col sm:flex-row justify-center sm:justify-start gap-3 sm:gap-4">
                            {buttons.map((btn, idx) => (
                                <motion.button
                                    key={idx}
                                    whileHover={{
                                        scale: 1.03,
                                        boxShadow: btn.shadow,
                                    }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={btn.onClick}
                                    className={`w-full sm:w-fit text-sm sm:text-base flex items-center justify-center gap-2 px-6 py-3.5 rounded-2xl whitespace-nowrap cursor-pointer font-semibold transition-all duration-300 ${btn.className}`}
                                >
                                    {btn.icon}
                                    <span>{btn.text}</span>
                                </motion.button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Features Footer */}
            <div className="container mx-auto px-4 sm:px-10 sm:pt-10 pt-4">
                <motion.div
                    className="flex flex-col md:flex-row-reverse justify-between sm:items-center gap-6 sm:gap-4"
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.2 }}
                >
                    {/* Feature Items */}
                    <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 w-full justify-around">
                        {features.map((feature, index) => (
                            <FeatureChip
                                key={index}
                                index={index}
                                icon={feature.icon}
                                title={feature.title}
                                description={feature.description}
                            />
                        ))}
                    </div>

                    {/* Partnership Badge */}
                    <motion.div
                        className="bg-blue-50/60 py-3.5 px-3 lg:px-6 rounded-2xl w-full md:w-auto border border-blue-100/60 cursor-pointer overflow-hidden"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 1.7 }}
                        whileHover={{
                            scale: 1.03,
                            boxShadow: "0 8px 24px -6px rgba(0,114,206,0.18)",
                            borderColor: "rgba(0,114,206,0.3)",
                        }}
                        whileTap={{ scale: 0.97 }}
                    >
                        <div className="flex items-center justify-center gap-3">
                            <img src="../../../media/HamrahAvval.svg" alt={t("partnership.logoAlt")} className="h-4" />
                            <p className="text-[#5D697F] text-sm border-s border-gray-300 ps-3">{t("partnership.text")}</p>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default HeroSection;
